// views/Dashboard/PainelExecutivo/sections/ceo/ParadasDoMes.tsx
//
// "Horas que a usina ficou sem gerar por dia e KPI de motivos" — como uma lista
// de PARADAS, cada uma numa frase: quando, quais usinas, quanto tempo, quanta
// energia deixou de entrar e por quê. Usinas que pararam juntas viram uma parada
// só ("as 5 de Vera Cruz"), não cinco linhas iguais.
//
// O porquê sai de `agruparParadas`: o motivo registrado pela equipe quando
// existe, e senão o que a telemetria permite deduzir (queda da rede, inversor
// fora, alarme). A tela escreve "provável" no que é dedução.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ClipboardCheck, Cpu, HelpCircle, Zap } from 'lucide-react';
import { ROTULO_CAUSA, horasPorCausa } from '../../../../../src/lib/dashboard/painelCeo';
import type { CausaParada, EventoParada, UsinaCeo } from '../../../../../src/lib/dashboard/painelCeo';
import { Bloco, Destaque, ErroBloco, LinhasCarregando, horas, kwhCurto, pct, useMontado, CURVA_SAIDA } from './ui';

/**
 * Uma cor por causa. Cinza para "sem explicação" — é ausência de informação, não
 * um tipo de defeito. As causas também levam ícone e nome: a cor nunca é a única pista.
 */
const ESTILO_CAUSA: Record<CausaParada, { cor: string; texto: string; fundo: string; Icone: React.ComponentType<{ size?: number; className?: string }> }> = {
  rede: { cor: '#3B82F6', texto: 'text-blue-700 dark:text-blue-300', fundo: 'bg-blue-50 dark:bg-blue-900/30', Icone: Zap },
  inversor: { cor: '#F2994A', texto: 'text-orange-700 dark:text-orange-300', fundo: 'bg-orange-50 dark:bg-orange-900/30', Icone: Cpu },
  alarme: { cor: '#D97706', texto: 'text-amber-700 dark:text-amber-300', fundo: 'bg-amber-50 dark:bg-amber-900/30', Icone: AlertTriangle },
  registrado: { cor: '#55702A', texto: 'text-lime-800 dark:text-lime-300', fundo: 'bg-lime-50 dark:bg-lime-900/30', Icone: ClipboardCheck },
  sem_explicacao: { cor: '#9CA3AF', texto: 'text-gray-600 dark:text-gray-300', fundo: 'bg-gray-100 dark:bg-gray-700/60', Icone: HelpCircle },
};

const quando = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}h`;
};

interface Props {
  eventos: EventoParada[];
  usinas: UsinaCeo[];
  loading: boolean;
  erro: string | null;
  nomeMes: string;
}

const MOSTRAR = 5;

const ParadasDoMes: React.FC<Props> = ({ eventos, usinas, loading, erro, nomeMes }) => {
  const [todas, setTodas] = useState(false);
  const causas = useMemo(() => horasPorCausa(eventos), [eventos]);
  const totalHoras = causas.reduce((t, c) => t + c.horas, 0);
  const energia = eventos.reduce((t, e) => t + e.energiaKwh, 0);
  const principal = causas[0];

  const nomeDe = useMemo(() => new Map(usinas.map((u) => [u.id, u])), [usinas]);
  const doConjunto = useMemo(() => {
    const m = new Map<string, number>();
    usinas.forEach((u) => { if (u.siteId) m.set(u.conjunto, (m.get(u.conjunto) ?? 0) + 1); });
    return m;
  }, [usinas]);

  /** "todas as 5 de Vera Cruz" quando o grupo inteiro parou; senão os nomes. */
  const quais = (e: EventoParada) => {
    const us = e.usinaIds.map((id) => nomeDe.get(id)).filter((u): u is UsinaCeo => !!u);
    const conj = us[0]?.conjunto;
    if (us.length > 1 && us.every((u) => u.conjunto === conj) && us.length === doConjunto.get(conj ?? '')) return `Todas as ${us.length} de ${conj}`;
    return us.map((u) => u.nome).join(', ');
  };

  const lista = todas ? eventos : eventos.slice(0, MOSTRAR);

  return (
    <Bloco
      titulo={loading || erro ? 'Paradas das usinas' : totalHoras === 0 ? `Nenhuma parada em ${nomeMes}` : `${horas(totalHoras)} de usina parada em ${nomeMes}`}
      subtitulo={
        loading ? 'Analisando a telemetria do mês…'
          : totalHoras === 0 ? 'Toda usina gerou no ritmo das vizinhas.'
          : `${eventos.length} ${eventos.length === 1 ? 'parada' : 'paradas'}. A maior parte foi ${ROTULO_CAUSA[principal.causa].toLowerCase()} (${pct(principal.parte)} das horas).`
      }
      destaque={loading || erro || totalHoras === 0 ? null : (
        <Destaque valor={kwhCurto(energia)} tom="text-orange-600 dark:text-orange-400" legenda="que deixaram de ser gerados (estimativa)" />
      )}
    >
      {erro ? (
        <ErroBloco mensagem={erro} />
      ) : loading ? (
        <LinhasCarregando linhas={4} rotulo="Juntando as paradas e os motivos…" />
      ) : totalHoras === 0 ? (
        <div className="flex items-start gap-3 px-5 pb-6 text-sm text-gray-600 dark:text-gray-300">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-500" />
          <p>Nenhuma usina parou em {nomeMes} até agora.</p>
        </div>
      ) : (
        <div className="px-5 pb-5">
          <MotivosEmBarra causas={causas} />

          <ul className="mt-5 divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-700/70 dark:border-gray-700/70">
            {lista.map((e) => {
              const est = ESTILO_CAUSA[e.causa];
              const deduzido = e.causa !== 'registrado' && e.causa !== 'sem_explicacao';
              return (
                <li key={e.chave} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 py-3.5 sm:grid-cols-[auto_1fr_auto]">
                  <span className={`row-span-2 mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg ${est.fundo}`} aria-hidden>
                    <est.Icone size={16} className={est.texto} />
                  </span>
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-gray-900 dark:text-white">
                      {e.titulo}
                      {deduzido && <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">deduzido</span>}
                      {e.emAberto && <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">em andamento</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{quais(e)}</span> · {quando(e.inicio)} → {e.emAberto ? 'agora' : quando(e.fim)}
                    </p>
                  </div>
                  <p className="col-start-2 text-xs tabular-nums text-gray-500 sm:col-start-3 sm:row-start-1 sm:text-right dark:text-gray-400">
                    <strong className="font-semibold text-gray-900 dark:text-white">{horas(e.duracaoH)}</strong>
                    {e.usinaIds.length > 1 && <> em {e.usinaIds.length} usinas</>}
                    {e.energiaKwh > 0 && <> · ≈ {kwhCurto(e.energiaKwh)}</>}
                  </p>
                  <p className="col-start-2 text-xs leading-relaxed text-gray-600 sm:col-span-2 dark:text-gray-300">{e.explicacao}</p>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-[11px] text-gray-500 dark:text-gray-400">
            {eventos.length > MOSTRAR ? (
              <button
                type="button"
                onClick={() => setTodas((t) => !t)}
                className="inline-flex items-center gap-1 font-semibold text-gray-600 transition-[color,transform] duration-150 hover:text-gray-900 active:scale-[0.97] dark:text-gray-300 dark:hover:text-white"
              >
                {todas ? 'Mostrar só as maiores' : `Ver todas as ${eventos.length} paradas`}
                <ChevronDown size={13} className={`transition-transform duration-200 ${todas ? 'rotate-180' : ''}`} />
              </button>
            ) : <span />}
            <span className="max-w-xl text-right">
              "Deduzido" é o que a telemetria indica. Quem confirma é a equipe, registrando o motivo em{' '}
              <Link to="/geradoras?aba=analise" className="font-semibold text-primary underline-offset-2 hover:underline dark:text-primary-light">Geradoras › Análise</Link>.
            </span>
          </div>
        </div>
      )}
    </Bloco>
  );
};

/** "KPI de motivos": as horas paradas divididas por causa, numa barra só. */
const MotivosEmBarra: React.FC<{ causas: ReturnType<typeof horasPorCausa> }> = ({ causas }) => {
  const montado = useMontado();
  return (
    <div>
      <div
        role="img"
        aria-label={causas.map((c) => `${ROTULO_CAUSA[c.causa]}: ${horas(c.horas)}`).join(', ')}
        className="flex h-3 gap-[2px] overflow-hidden rounded-full motion-reduce:!transition-none"
        style={{ clipPath: `inset(0 ${montado ? 0 : 100}% 0 0 round 9999px)`, transition: `clip-path 700ms ${CURVA_SAIDA}` }}
      >
        {causas.map((c) => (
          <span key={c.causa} className="h-full" style={{ width: `${c.parte * 100}%`, background: ESTILO_CAUSA[c.causa].cor }} />
        ))}
      </div>
      <p className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-600 dark:text-gray-300">
        {causas.map((c) => {
          const est = ESTILO_CAUSA[c.causa];
          return (
            <span key={c.causa} className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: est.cor }} aria-hidden />
              {ROTULO_CAUSA[c.causa]}
              <strong className="font-semibold tabular-nums text-gray-900 dark:text-white">{horas(c.horas)}</strong>
              <span className="tabular-nums text-gray-400">{pct(c.parte)}</span>
            </span>
          );
        })}
      </p>
    </div>
  );
};

export default ParadasDoMes;
